import React, { FC } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { TextField } from 'components/generic';
import { useCodeInputStep } from 'hooks/auth';

interface CodeInputStepProps {
  onClose: () => void;
  prevStep: () => void;
}

const CodeInputStep: FC<CodeInputStepProps> = ({ onClose, prevStep }) => {
  const {
    t,
    control,
    verifyIsLoading,
    isTimer,
    hint,
    handleGetCode,
    loginValue,
    handleLogIn,
    trySendAgain,
    sendAgain,
    mask,
  } = useCodeInputStep(onClose);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '368px',
      }}
    >
      <Box>
        <Typography sx={{ marginBottom: '12px' }} variant="h4">
          {t('confirmation_code')}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            marginBottom: '45px',
            lineHeight: '21px',
          }}
        >
          {hint} {loginValue}
        </Typography>
      </Box>
      <Box sx={{ marginBottom: '30px' }}>
        <TextField
          control={control}
          name="code"
          label={t('login.code')}
          mask={mask}
          maskPlaceholder={null}
          required
          endIcon={
            verifyIsLoading ? (
              <CircularProgress size={25} color="primary" />
            ) : (
              <></>
            )
          }
        />
      </Box>
      <Box sx={{ flexGrow: 1 }}>
        {isTimer ? (
          <Typography sx={{ fontSize: '13px', color: 'grey.700' }}>
            {trySendAgain}
          </Typography>
        ) : (
          <Typography
            sx={{
              fontSize: '13px',
              lineHeight: '16px',
              color: 'secondary.main',
              cursor: 'pointer',
              alignSelf: 'flex-start',
            }}
            onClick={handleGetCode}
          >
            {sendAgain}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          width: '100%',
        }}
        gap={2}
      >
        <Button
          sx={{
            padding: '10px 0',
            textTransform: 'inherit',
            fontSize: '18px',
            lineHeight: '21px',
            borderRadius: '6px',
            fontWeight: '300',
            color: 'text.primary',
            boxShadow: 'none',
          }}
          variant="text"
          color="inherit"
          onClick={prevStep}
          fullWidth
        >
          {t('back')}
        </Button>

        <Button
          sx={{
            padding: '15px 0',
            textTransform: 'inherit',
            fontSize: '18px',
            lineHeight: '21px',
            borderRadius: '6px',
            fontWeight: '300',
            color: 'primary.contrastText',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
            },
            '&:focus': {
              boxShadow: 'none',
            },
          }}
          variant="contained"
          color="primary"
          fullWidth
          type="submit"
          onClick={handleLogIn}
        >
          {t('continue')}
        </Button>
      </Box>
    </Box>
  );
};

export default CodeInputStep;
